import React, { ReactElement } from 'react';
import { GenderEnum } from 'types/generated/client';
import { toSentenceCase } from 'utils/shared/string/toSentenceCase';
import BoltIcon from 'svg/BoltIcon';
import { LessonPageModeEnum } from 'screens/ExploreLessons/enums';
import { sortOptions } from 'screens/ExploreRemote/utils';
import TabSwitcher, { type TabOption } from 'components/TabSwitcher';
import FilterBox from 'components/forms/FilterBox/FilterBox';
import { SliderNumberRange } from 'components/forms/Slider';
import classNames from 'styles/utils/classNames';
import CertificationSelect from './CertificationSelect';
import {
  CERTIFICATIONS_BY_SPORT,
  CoachAvailabilityFilter,
  MAX_PRICE,
  MIN_PRICE,
  WORKS_WITH_OPTIONS,
} from '../constants';
import { CertificationEnum, LessonFiltersProps } from '../props';
import { VENUE_DISTANCE_IMPERIAL_OPTIONS } from '../utils';

// Constants

export const SectionTitle = ({ children }: { children: React.ReactNode }): ReactElement => {
  return (
    <p className="typography-product-subheading mb-2 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
      {children}
    </p>
  );
};

/**
 * Hairline between filter sections. Every filters modal in Figma separates its
 * sections with one; as a flex child of a `gap-ds-xl` column it lands 24px from each
 * neighbour, matching the design. Pass the same responsive visibility class as the
 * section it follows so a hidden section does not leave a stray line.
 */
export const FilterDivider = ({ className }: { className?: string }): ReactElement => {
  return (
    <div
      className={classNames(
        'border-t border-color-border-input-lightmode dark:border-color-border-input-darkmode',
        className,
      )}
    />
  );
};

export const AVAILABILITY_OPTIONS: TabOption<CoachAvailabilityFilter>[] = [
  { label: 'All', value: CoachAvailabilityFilter.All },
  {
    label: 'Instant book',
    value: CoachAvailabilityFilter.InstantBook,
    Icon: BoltIcon,
    iconClassName: 'h-4 w-4',
  },
];

const LessonFilters: React.FC<LessonFiltersProps> = ({
  selectedWorksWith,
  setSelectedWorksWith,
  selectedGender,
  setSelectedGender,
  priceMinimum,
  setPriceMinimum,
  priceMaximum,
  setPriceMaximum,
  selectedCertification,
  setSelectedCertification,
  distance,
  setDistance,
  sortBy,
  setSortBy,
  selectedSport,
  pageMode,
  availability,
  setAvailability,
}) => {
  const certifications = CERTIFICATIONS_BY_SPORT[selectedSport] ?? [];

  // Instant book is only a meaningful split on the explore marketplace — the coach
  // landing pages render a single profile.
  const hasAvailabilityTabs = pageMode === LessonPageModeEnum.EXPLORE;

  return (
    <div className="flex flex-auto flex-col gap-ds-xl overflow-y-auto px-4 py-6 md:px-ds-3xl">
      {hasAvailabilityTabs && (
        <div className="lg:hidden">
          <TabSwitcher
            options={AVAILABILITY_OPTIONS}
            selectedValue={availability}
            onValueChange={setAvailability}
            keepIconColor
          />
        </div>
      )}

      {hasAvailabilityTabs && <FilterDivider className="md:hidden" />}
      <div className="md:hidden">
        <SectionTitle>Distance</SectionTitle>
        <div className="grid grid-cols-3 gap-4">
          {VENUE_DISTANCE_IMPERIAL_OPTIONS.map((newDistance) => (
            <FilterBox
              key={newDistance.id}
              label={newDistance.name}
              onClick={() => setDistance(newDistance)}
              isSelected={distance.id === newDistance.id}
            />
          ))}
        </div>
      </div>

      {/* Without the tabs above it, the only thing preceding Sort is Distance, which
          stops at `md` — so the divider has to stop there too. */}
      <FilterDivider className={hasAvailabilityTabs ? 'lg:hidden' : 'md:hidden'} />
      <div className="lg:hidden">
        <SectionTitle>Sort By</SectionTitle>
        {/* Chips, not the header's dropdown — inside the modal every filter is a grid
            of tappable boxes, matching Distance directly above. */}
        <div className="grid grid-cols-3 gap-4">
          {sortOptions.map((option) => (
            <FilterBox
              key={option.value}
              label={option.label}
              onClick={() => setSortBy(option.value)}
              isSelected={option.value === sortBy}
            />
          ))}
        </div>
      </div>

      {/* Certifications Section */}
      {pageMode === LessonPageModeEnum.EXPLORE && certifications.length > 0 && (
        <div>
          <FilterDivider className="mb-ds-xl lg:hidden" />
          <SectionTitle>Certifications</SectionTitle>
          <div className="mt-3 grid grid-cols-4 gap-4 max-sm:grid-cols-3">
            {certifications.map((certification) => (
              <CertificationSelect
                key={certification.value}
                title={certification.title}
                onClick={() =>
                  setSelectedCertification(
                    selectedCertification === certification.value ? null : certification.value,
                  )
                }
                isSelected={selectedCertification === certification.value}
                imgSrc={certification.imageSrc}
              />
            ))}
          </div>
        </div>
      )}

      {/* Gender Section. Outside EXPLORE the certification sections above it are not
          rendered at all, so at `lg` — where Sort has moved to the header — Gender
          becomes the first section and must not carry a divider. */}
      <FilterDivider className={hasAvailabilityTabs ? undefined : 'lg:hidden'} />
      <div>
        <SectionTitle>Gender</SectionTitle>
        <div className="mt-3 grid grid-cols-2 gap-4">
          {[GenderEnum.Male, GenderEnum.Female].map((gender) => (
            <FilterBox
              key={gender}
              label={toSentenceCase(gender)}
              onClick={() => setSelectedGender(gender)}
              isSelected={gender === selectedGender}
            />
          ))}
        </div>
      </div>

      {/* Price Range */}
      <FilterDivider />
      <div>
        <SectionTitle>Price</SectionTitle>
        <div className="pt-10">
          <SliderNumberRange
            rangeMinimum={MIN_PRICE}
            rangeMaximum={MAX_PRICE}
            valueMinimum={priceMinimum}
            valueMaximum={priceMaximum}
            setValueMinumum={(newMinimum) => setPriceMinimum(newMinimum)}
            setValueMaximum={(newMaximum) => setPriceMaximum(newMaximum)}
            step={1}
            decimals={0}
            prefix="$"
            isMaximumOpenEnded
          />
        </div>
      </div>

      {/* Works With Section */}
      <FilterDivider />
      <div>
        <SectionTitle>Works with</SectionTitle>
        <div className="mt-3 grid grid-cols-3 gap-4">
          {WORKS_WITH_OPTIONS.map((option) => (
            <FilterBox
              key={option.id}
              label={option.label}
              onClick={() =>
                setSelectedWorksWith(option.id === selectedWorksWith ? null : option.id)
              }
              isSelected={selectedWorksWith === option.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LessonFilters;
